import { config } from './config.ts';
import { initGpu, showFallback, WebGpuUnavailableError, type GpuContext } from './gpu/device.ts';
import { resolveSeed } from './util/prng.ts';
import { generate } from './gen/orchestrator.ts';
import { createFrameRenderer } from './render/frame.ts';
import { createCameraChoreographer } from './camera/choreographer.ts';
import { createFreeFlyCamera, type FreeFlyInput } from './camera/free-fly.ts';
import { createTerrainSampler } from './camera/terrain-sampler.ts';
import { createCars } from './cars/cars.ts';
import type { Mat4 } from './util/mat.ts';
import type { Vec3 } from './util/vec.ts';
import type {
  BiomeProbe,
  BuildingsProbe,
  CarsProbe,
  CityGenDebug,
  DebugCameraPose,
  HeightmapProbe,
  RoadsProbe,
  TreesProbe,
} from './types.d.ts';

/** Zero input for driving the fly camera from a pinned pose (Tier 3 overrides). */
const STILL_INPUT: FreeFlyInput = {
  forward: 0,
  strafe: 0,
  vertical: 0,
  mouseDX: 0,
  mouseDY: 0,
  fast: false,
};

/** eye→target vector to the fly camera's yaw/pitch convention
 *  (`dir = [cosP·sinYaw, sinP, -cosP·cosYaw]`, so yaw = atan2(dx, -dz)). */
function aimAt(
  eye: readonly [number, number, number],
  lookAt: readonly [number, number, number],
): { yaw: number; pitch: number } {
  const dx = lookAt[0] - eye[0];
  const dy = lookAt[1] - eye[1];
  const dz = lookAt[2] - eye[2];
  return { yaw: Math.atan2(dx, -dz), pitch: Math.atan2(dy, Math.hypot(dx, dz)) };
}

async function bootstrap(): Promise<void> {
  const canvas = document.getElementById('canvas') as HTMLCanvasElement | null;
  if (!canvas) throw new Error('missing #canvas');

  const dpr = Math.min(window.devicePixelRatio, 2);
  const resizeCanvas = (): void => {
    const w = Math.max(1, Math.floor(canvas.clientWidth * dpr));
    const h = Math.max(1, Math.floor(canvas.clientHeight * dpr));
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
      return;
    }
  };
  resizeCanvas();

  let gpu: GpuContext;
  try {
    gpu = await initGpu(canvas);
  } catch (err) {
    if (err instanceof WebGpuUnavailableError) {
      console.warn('[city-gen]', err.message);
      showFallback();
      return;
    }
    throw err;
  }

  let deviceLost = false;
  void gpu.device.lost.then((info) => {
    if (info.reason === 'destroyed') return;
    deviceLost = true;
    showFallback('GPU device lost — reload to retry.');
  });

  const rawHash = window.location.hash.slice(1);
  const seedInput = rawHash.startsWith('seed=') ? rawHash.slice(5) : rawHash;
  const seed = resolveSeed(seedInput || config.fallbackSeed);
  console.info(`[city-gen] seed=0x${seed.toString(16).padStart(8, '0')}`);

  const tGen0 = performance.now();
  const gen = await generate(gpu.device, seed);
  console.info(`[city-gen] gen complete in ${(performance.now() - tGen0).toFixed(1)}ms`);

  // Tier 3 visual-inspection overrides, driven from scripts/visual-snapshot.ts
  // through `window.__cityGen`. Both null = normal live behaviour. They only
  // touch these locals; the frame loop reads them, so the setters can be defined
  // before the cameras exist.
  let camOverride: DebugCameraPose | null = null;
  let timeOverride: number | null = null;

  const debug: CityGenDebug = {
    frameCount: 0,
    device: gpu.device,
    heightmap: gen.heightmap.texture,
    canvas,
    setCamera: (pose) => {
      camOverride = pose;
    },
    setTime: (seconds) => {
      timeOverride = seconds;
    },
  };
  window.__cityGen = debug;

  const probeFlag = new URLSearchParams(window.location.search).has('probe');
  if (probeFlag) {
    // Each probe kicks its own copy + mapAsync; overlapping the waits cuts probe
    // wall-time roughly in half vs awaiting them serially.
    const [heightmap, biome, roads, buildings, trees] = await Promise.all([
      probeHeightmap(gpu.device, gen.heightmap.texture),
      probeBiome(gpu.device, gen.biome.texture),
      probeRoads(gpu.device, gen.roads.mask),
      probeBuildings(gpu.device, gen.buildings.countBuffer, gen.buildings.maxInstances),
      probeTrees(gpu.device, gen.trees.countBuffer, gen.trees.maxInstances),
    ]);
    // Diagnostic: how much of the occupancy mask is actually stamped. If this is
    // ~0%, the flatten write isn't landing; if it matches building coverage, the
    // mask is fine and the tree gate is the suspect.
    await probeOccupancy(gpu.device, gen.occupancy);
    debug.probes = { heightmap, biome, roads, buildings, trees };
  }

  const terrainSampler = await createTerrainSampler(gpu.device, gen.heightmap.texture);
  const cars = await createCars(gpu.device, gen.roads.mask, {
    seed,
    heightSampler: terrainSampler.sampleHeight,
  });
  if (probeFlag) {
    // `cars.maxInstances` is 0 when the no-drivable-cells early-return fires,
    // else `config.carInstanceCount`. `debug.probes` is non-null here because
    // the earlier `probeFlag` block populated it.
    const carsProbe: CarsProbe = { count: cars.maxInstances, cap: config.carInstanceCount };
    debug.probes!.cars = carsProbe;
    console.info(`[city-gen] cars probe count=${carsProbe.count} (cap=${carsProbe.cap})`);
  }
  const renderer = createFrameRenderer(gpu, gen, cars);
  const camera = createCameraChoreographer({
    aspect: canvas.width / canvas.height,
    seed,
    heightSampler: terrainSampler.sampleHeight,
  });
  const flyCam = createFreeFlyCamera({ heightSampler: terrainSampler.sampleHeight });

  const onResize = (): void => {
    resizeCanvas();
    renderer.resize();
  };
  window.addEventListener('resize', onResize);

  // Random-seed button: pick a fresh seed and reload. Generation is one-shot at
  // startup and keyed off location.hash, so a reload with a new #seed= is the
  // clean way to regenerate. crypto (not Math.random) keeps the determinism rule
  // intact — this only chooses which deterministic world to show.
  document.getElementById('seed-btn')?.addEventListener('click', () => {
    const buf = new Uint32Array(2);
    crypto.getRandomValues(buf);
    const token = buf[0]!.toString(36) + buf[1]!.toString(36);
    history.replaceState(null, '', '#seed=' + token);
    window.location.reload();
  });

  let dragging = false;
  let yawOffset = 0;
  let pitchOffset = 0;
  let fovScale = 1;
  let lastMouseX = 0;
  let lastMouseY = 0;

  let flyMode = false;
  const flyInput = { forward: 0, strafe: 0, vertical: 0, mouseDX: 0, mouseDY: 0, fast: false };
  const keys = new Set<string>();
  const updateAxes = (): void => {
    flyInput.forward  = (keys.has('KeyW') ? 1 : 0) - (keys.has('KeyS') ? 1 : 0);
    flyInput.strafe   = (keys.has('KeyD') ? 1 : 0) - (keys.has('KeyA') ? 1 : 0);
    flyInput.vertical = (keys.has('KeyE') ? 1 : 0) - (keys.has('KeyQ') ? 1 : 0);
    flyInput.fast     = keys.has('ShiftLeft') || keys.has('ShiftRight');
  };

  canvas.addEventListener('mousedown', (e) => {
    if (e.button === 2) {
      e.preventDefault();
      if (document.pointerLockElement === canvas) document.exitPointerLock();
      else {
        // Returns a promise in Chromium that rejects on e.g. rapid toggles or
        // user-gesture rules; a rejection just means we stay in cinematic mode.
        Promise.resolve(canvas.requestPointerLock() as void | Promise<void>).catch(() => {});
      }
      return;
    }
    if (e.button !== 0) return;
    if (flyMode) return;
    dragging = true;
    lastMouseX = e.clientX;
    lastMouseY = e.clientY;
    canvas.style.cursor = 'grabbing';
    e.preventDefault();
  });
  canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  window.addEventListener('mouseup', (e) => {
    if (e.button !== 0) return;
    if (!dragging) return;
    dragging = false;
    canvas.style.cursor = '';
  });
  window.addEventListener('mousemove', (e) => {
    if (flyMode) {
      flyInput.mouseDX += e.movementX;
      flyInput.mouseDY += e.movementY;
      return;
    }
    if (!dragging) return;
    const dx = e.clientX - lastMouseX;
    const dy = e.clientY - lastMouseY;
    lastMouseX = e.clientX;
    lastMouseY = e.clientY;
    yawOffset -= dx * config.mouseSens;
    pitchOffset -= dy * config.mouseSens;
    if (pitchOffset >  config.mousePitchLimit) pitchOffset =  config.mousePitchLimit;
    if (pitchOffset < -config.mousePitchLimit) pitchOffset = -config.mousePitchLimit;
  });
  canvas.addEventListener('wheel', (e) => {
    e.preventDefault();
    if (flyMode) return;
    // Normalize across deltaMode (0=pixel, 1=line, 2=page).
    const lines = e.deltaMode === 1 ? e.deltaY : e.deltaMode === 2 ? e.deltaY * 30 : e.deltaY / 16;
    fovScale *= Math.exp(lines * config.zoomStep);
    if (fovScale < config.zoomFovMin) fovScale = config.zoomFovMin;
    if (fovScale > config.zoomFovMax) fovScale = config.zoomFovMax;
  }, { passive: false });

  // Fly-mode enter/exit, shared by the desktop pointer-lock path and the mobile
  // Fly button. Seeds the fly camera from the current cinematic pose so there's
  // no snap on entry.
  const flyToggle = document.getElementById('fly-toggle');
  const joystick = document.getElementById('joystick');
  const joyKnob = joystick?.querySelector('.knob') as HTMLElement | null;

  const enterFly = (): void => {
    if (flyMode) return;
    const ld = camera.lookDir;
    const yaw = Math.atan2(ld[0], -ld[2]);
    const pitch = Math.asin(Math.max(-1, Math.min(1, ld[1])));
    flyCam.setPose(camera.eye, yaw, pitch);
    flyMode = true;
    if (dragging) {
      dragging = false;
      canvas.style.cursor = '';
    }
    flyToggle?.classList.add('active');
    joystick?.classList.add('show');
  };
  const exitFly = (): void => {
    if (!flyMode) return;
    flyMode = false;
    keys.clear();
    flyInput.forward = flyInput.strafe = flyInput.vertical = 0;
    flyInput.fast = false;
    flyInput.mouseDX = flyInput.mouseDY = 0;
    flyToggle?.classList.remove('active');
    joystick?.classList.remove('show');
    if (joyKnob) joyKnob.style.transform = '';
  };

  document.addEventListener('pointerlockchange', () => {
    if (document.pointerLockElement === canvas) enterFly();
    else exitFly();
  });

  window.addEventListener('keydown', (e) => {
    if (!flyMode) return;
    keys.add(e.code);
    updateAxes();
  });
  window.addEventListener('keyup', (e) => {
    if (!keys.has(e.code)) return;
    keys.delete(e.code);
    updateAxes();
  });
  window.addEventListener('blur', () => {
    keys.clear();
    updateAxes();
  });

  // ---- Touch controls (mobile) ----
  // Cinematic: one-finger drag looks around (yaw/pitch offset), two-finger pinch
  // zooms (fovScale). Free-fly (after tapping Fly): one-finger drag steers, the
  // thumbstick drives forward/strafe. All share the same state the mouse path uses.
  const touchDist = (a: Touch, b: Touch): number =>
    Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

  let lookTouchId: number | null = null;
  let touchLastX = 0;
  let touchLastY = 0;
  let pinchRef = 0; // active-pinch reference distance in px; 0 = not pinching

  flyToggle?.addEventListener('click', () => {
    // Drop any in-flight look/pinch finger so the mode switch starts clean.
    lookTouchId = null;
    pinchRef = 0;
    if (flyMode) exitFly();
    else enterFly();
  });

  canvas.addEventListener('touchstart', (e) => {
    e.preventDefault();
    if (!flyMode && e.touches.length >= 2) {
      pinchRef = touchDist(e.touches[0]!, e.touches[1]!);
      lookTouchId = null; // suspend look while pinching
      return;
    }
    if (lookTouchId === null) {
      const t = e.changedTouches[0];
      if (t) {
        lookTouchId = t.identifier;
        touchLastX = t.clientX;
        touchLastY = t.clientY;
      }
    }
  }, { passive: false });

  canvas.addEventListener('touchmove', (e) => {
    e.preventDefault();
    if (pinchRef > 0 && e.touches.length >= 2) {
      const d = touchDist(e.touches[0]!, e.touches[1]!);
      if (d > 0) {
        fovScale *= pinchRef / d; // fingers spread → d grows → zoom in
        if (fovScale < config.zoomFovMin) fovScale = config.zoomFovMin;
        if (fovScale > config.zoomFovMax) fovScale = config.zoomFovMax;
        pinchRef = d;
      }
      return;
    }
    if (lookTouchId === null) return;
    let t: Touch | undefined;
    for (let i = 0; i < e.changedTouches.length; i++) {
      const c = e.changedTouches[i]!;
      if (c.identifier === lookTouchId) { t = c; break; }
    }
    if (!t) return;
    const dx = t.clientX - touchLastX;
    const dy = t.clientY - touchLastY;
    touchLastX = t.clientX;
    touchLastY = t.clientY;
    if (flyMode) {
      flyInput.mouseDX += dx;
      flyInput.mouseDY += dy;
    } else {
      yawOffset -= dx * config.mouseSens;
      pitchOffset -= dy * config.mouseSens;
      if (pitchOffset >  config.mousePitchLimit) pitchOffset =  config.mousePitchLimit;
      if (pitchOffset < -config.mousePitchLimit) pitchOffset = -config.mousePitchLimit;
    }
  }, { passive: false });

  const onCanvasTouchEnd = (e: TouchEvent): void => {
    for (let i = 0; i < e.changedTouches.length; i++) {
      if (e.changedTouches[i]!.identifier === lookTouchId) lookTouchId = null;
    }
    if (e.touches.length < 2) pinchRef = 0;
    // Cinematic only: hand look to a remaining finger so releasing one pinch
    // finger doesn't freeze look. In fly mode this must NOT run — `e.touches`
    // is global, so it would adopt the joystick finger (whose events fire on the
    // joystick, never the canvas), permanently wedging steering until refresh.
    if (!flyMode && lookTouchId === null && e.touches.length >= 1) {
      const t = e.touches[0]!;
      lookTouchId = t.identifier;
      touchLastX = t.clientX;
      touchLastY = t.clientY;
    }
  };
  canvas.addEventListener('touchend', onCanvasTouchEnd);
  canvas.addEventListener('touchcancel', onCanvasTouchEnd);

  // Virtual thumbstick → forward/strafe while in free-fly.
  if (joystick && joyKnob) {
    const joyRadius = 50; // px of knob travel
    let joyId: number | null = null;
    const moveKnob = (clientX: number, clientY: number): void => {
      const rect = joystick.getBoundingClientRect();
      const ox = clientX - (rect.left + rect.width / 2);
      const oy = clientY - (rect.top + rect.height / 2);
      const len = Math.hypot(ox, oy);
      const scale = len > joyRadius ? joyRadius / len : 1;
      const kx = ox * scale;
      const ky = oy * scale;
      joyKnob.style.transform = `translate(${kx}px, ${ky}px)`;
      flyInput.strafe = kx / joyRadius;   // right = +
      flyInput.forward = -ky / joyRadius; // up = forward
    };
    const resetKnob = (): void => {
      joyId = null;
      flyInput.forward = 0;
      flyInput.strafe = 0;
      joyKnob.style.transform = '';
    };
    joystick.addEventListener('touchstart', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (joyId !== null) return;
      const t = e.changedTouches[0];
      if (!t) return;
      joyId = t.identifier;
      moveKnob(t.clientX, t.clientY);
    }, { passive: false });
    joystick.addEventListener('touchmove', (e) => {
      e.preventDefault();
      e.stopPropagation();
      for (let i = 0; i < e.changedTouches.length; i++) {
        const t = e.changedTouches[i]!;
        if (t.identifier === joyId) { moveKnob(t.clientX, t.clientY); break; }
      }
    }, { passive: false });
    const joyEnd = (e: TouchEvent): void => {
      for (let i = 0; i < e.changedTouches.length; i++) {
        if (e.changedTouches[i]!.identifier === joyId) { resetKnob(); break; }
      }
    };
    joystick.addEventListener('touchend', joyEnd);
    joystick.addEventListener('touchcancel', joyEnd);
  }

  const startMs = performance.now();
  let lastFrameMs = startMs;

  const loadingEl = document.getElementById('loading');
  const dismissLoading = (): void => {
    if (!loadingEl) return;
    loadingEl.classList.add('gone');
    setTimeout(() => loadingEl.remove(), 500);
  };

  const frame = (): void => {
    if (deviceLost) return;
    const nowMs = performance.now();
    const frozen = timeOverride !== null;
    const t = timeOverride ?? (nowMs - startMs) / 1000;
    const dt = frozen ? 0 : Math.min(0.1, (nowMs - lastFrameMs) / 1000);
    lastFrameMs = nowMs;
    debug.frameCount++;

    const aspect = canvas.width / canvas.height;
    let viewProj: Mat4;
    let eye: Vec3;
    if (camOverride) {
      // Re-pin every frame so the pose can't drift, then update with zero input
      // purely to rebuild viewProj against the current aspect.
      const aim = camOverride.lookAt
        ? aimAt(camOverride.eye, camOverride.lookAt)
        : { yaw: camOverride.yaw ?? 0, pitch: camOverride.pitch ?? 0 };
      flyCam.setPose(camOverride.eye, aim.yaw, aim.pitch);
      flyCam.setFovScale(camOverride.fovScale ?? 1);
      flyCam.update(0, aspect, STILL_INPUT);
      // Drain look deltas the pointer-lock handler may still be accumulating, so
      // clearing the override doesn't snap the camera by everything banked here.
      flyInput.mouseDX = 0;
      flyInput.mouseDY = 0;
      viewProj = flyCam.viewProj;
      eye = flyCam.eye;
    } else if (flyMode) {
      flyCam.setFovScale(1);
      flyCam.update(dt, aspect, flyInput);
      flyInput.mouseDX = 0;
      flyInput.mouseDY = 0;
      viewProj = flyCam.viewProj;
      eye = flyCam.eye;
    } else {
      if (!dragging) {
        const decay = Math.exp(-dt * config.mouseReleaseDecay);
        yawOffset *= decay;
        pitchOffset *= decay;
      }
      camera.update(t, aspect, { yaw: yawOffset, pitch: pitchOffset, fovScale });
      viewProj = camera.viewProj;
      eye = camera.eye;
    }
    // Frozen clock ⇒ leave the car instance buffer exactly as last uploaded.
    // `update(0)` wouldn't move them, but it still ticks the off-road counters
    // that eventually respawn a car, which would drift across held frames.
    if (!frozen) cars.update(dt);
    renderer.render({ viewProj, eye }, t);

    if (debug.frameCount === 1) dismissLoading();

    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);
}

async function probeHeightmap(device: GPUDevice, tex: GPUTexture): Promise<HeightmapProbe> {
  const w = tex.width;
  const h = tex.height;
  // 256-byte alignment for bytesPerRow when copying.
  const bpr = Math.ceil((w * 4) / 256) * 256;
  const rb = device.createBuffer({ size: bpr * h, usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ });
  const enc = device.createCommandEncoder();
  enc.copyTextureToBuffer({ texture: tex }, { buffer: rb, bytesPerRow: bpr }, { width: w, height: h, depthOrArrayLayers: 1 });
  device.queue.submit([enc.finish()]);
  await rb.mapAsync(GPUMapMode.READ);
  const raw = new Float32Array(rb.getMappedRange().slice(0));
  rb.unmap();
  const stride = bpr / 4;
  const data = new Float32Array(w * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) data[y * w + x] = raw[y * stride + x] ?? 0;
  let min = Infinity, max = -Infinity, sum = 0;
  const histogram = new Uint32Array(10);
  for (const v of data) {
    if (v < min) min = v; if (v > max) max = v; sum += v;
    histogram[Math.min(9, Math.max(0, Math.floor(v * 10)))]!++;
  }
  const mean = sum / data.length;
  const probe: HeightmapProbe = {
    width: w,
    height: h,
    min,
    max,
    mean,
    histogram: Array.from(histogram),
  };
  console.info(`[city-gen] heightmap probe ${w}×${h} min=${min.toFixed(3)} max=${max.toFixed(3)} mean=${mean.toFixed(3)} histogram=[${probe.histogram.map((c: number) => (c / data.length * 100).toFixed(1) + '%').join(' ')}]`);
  rb.destroy();
  return probe;
}

async function probeBiome(device: GPUDevice, tex: GPUTexture): Promise<BiomeProbe> {
  const w = tex.width, h = tex.height;
  const bpr = Math.ceil((w * 4) / 256) * 256;
  const rb = device.createBuffer({ size: bpr * h, usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ });
  const enc = device.createCommandEncoder();
  enc.copyTextureToBuffer({ texture: tex }, { buffer: rb, bytesPerRow: bpr }, { width: w, height: h, depthOrArrayLayers: 1 });
  device.queue.submit([enc.finish()]);
  await rb.mapAsync(GPUMapMode.READ);
  const raw = new Uint32Array(rb.getMappedRange().slice(0));
  rb.unmap();
  const stride = bpr / 4;
  const counts = new Array<number>(8).fill(0);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const id = raw[y * stride + x] ?? 0;
    if (id < 8) counts[id]!++;
  }
  const names = ['ocean', 'beach', 'lowland', 'forest', 'urban', 'hill', 'rock', 'snow'];
  const total = w * h;
  const countsByName: Record<string, number> = {};
  const percentByName: Record<string, number> = {};
  for (let i = 0; i < names.length; i++) {
    const name = names[i]!;
    const c = counts[i]!;
    countsByName[name] = c;
    percentByName[name] = c / total;
  }
  console.info(`[city-gen] biome probe ${names.map((n, i) => `${n}=${(counts[i]! / total * 100).toFixed(1)}%`).join(' ')}`);
  rb.destroy();
  return { width: w, height: h, countsByName, percentByName };
}

async function probeRoads(device: GPUDevice, tex: GPUTexture): Promise<RoadsProbe> {
  const w = tex.width, h = tex.height;
  const bpr = Math.ceil((w * 4) / 256) * 256;
  const rb = device.createBuffer({ size: bpr * h, usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ });
  const enc = device.createCommandEncoder();
  enc.copyTextureToBuffer({ texture: tex }, { buffer: rb, bytesPerRow: bpr }, { width: w, height: h, depthOrArrayLayers: 1 });
  device.queue.submit([enc.finish()]);
  await rb.mapAsync(GPUMapMode.READ);
  const raw = new Float32Array(rb.getMappedRange().slice(0));
  rb.unmap();
  const stride = bpr / 4;
  let nonzero = 0, major = 0, minor = 0;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const v = raw[y * stride + x] ?? 0;
    if (v > 0.001) nonzero++;
    if (v > 0.9) major++;
    else if (v > 0.3) minor++;
  }
  const total = w * h;
  const coverage = nonzero / total;
  const majorFrac = major / total;
  const minorFrac = minor / total;
  console.info(`[city-gen] roads probe coverage=${(coverage * 100).toFixed(2)}% major=${(majorFrac * 100).toFixed(2)}% minor=${(minorFrac * 100).toFixed(2)}%`);
  rb.destroy();
  return { width: w, height: h, coverage, major: majorFrac, minor: minorFrac };
}

async function probeOccupancy(device: GPUDevice, tex: GPUTexture): Promise<void> {
  const w = tex.width, h = tex.height;
  const bpr = Math.ceil((w * 4) / 256) * 256;
  const rb = device.createBuffer({ size: bpr * h, usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ });
  const enc = device.createCommandEncoder();
  enc.copyTextureToBuffer({ texture: tex }, { buffer: rb, bytesPerRow: bpr }, { width: w, height: h, depthOrArrayLayers: 1 });
  device.queue.submit([enc.finish()]);
  await rb.mapAsync(GPUMapMode.READ);
  const raw = new Float32Array(rb.getMappedRange().slice(0));
  rb.unmap();
  const stride = bpr / 4;
  let nonzero = 0, max = 0;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const v = raw[y * stride + x] ?? 0;
    if (v > 0.001) nonzero++;
    if (v > max) max = v;
  }
  const total = w * h;
  console.info(`[city-gen] occupancy probe ${w}×${h} stamped=${(nonzero / total * 100).toFixed(3)}% max=${max.toFixed(3)} (0% ⇒ flatten write not landing)`);
  rb.destroy();
}

async function probeBuildings(device: GPUDevice, countBuffer: GPUBuffer, cap: number): Promise<BuildingsProbe> {
  const rb = device.createBuffer({ size: 16, usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ });
  const enc = device.createCommandEncoder();
  enc.copyBufferToBuffer(countBuffer, 0, rb, 0, 16);
  device.queue.submit([enc.finish()]);
  await rb.mapAsync(GPUMapMode.READ);
  const arr = new Uint32Array(rb.getMappedRange().slice(0));
  rb.unmap();
  const count = arr[0] ?? 0;
  console.info(`[city-gen] buildings probe count=${count} (cap=${cap})`);
  rb.destroy();
  return { count, cap };
}

async function probeTrees(device: GPUDevice, countBuffer: GPUBuffer, cap: number): Promise<TreesProbe> {
  const rb = device.createBuffer({ size: 16, usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ });
  const enc = device.createCommandEncoder();
  enc.copyBufferToBuffer(countBuffer, 0, rb, 0, 16);
  device.queue.submit([enc.finish()]);
  await rb.mapAsync(GPUMapMode.READ);
  const arr = new Uint32Array(rb.getMappedRange().slice(0));
  rb.unmap();
  const count = arr[0] ?? 0;
  console.info(`[city-gen] trees probe count=${count} (cap=${cap})`);
  rb.destroy();
  return { count, cap };
}

bootstrap().catch((err) => {
  console.error('[city-gen] fatal:', err);
  showFallback('Bootstrap failed — see console.');
});
